import {AgentIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback, useMemo, useRef} from 'react'
import type {ResolutionReasonType} from '../../conversation-types'
import type {LayoutStoreState} from '@github-ui/pull-requests/layout-store'
import {useIsInUnifiedBatch, useUnifiedBatchActions} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useOpenManageBatchPanel} from '@github-ui/pull-requests/use-open-manage-batch-panel'
import {ResolveThreadSplitButton} from './ResolveThreadSplitButton'
import {
  FixWithCopilotDialog,
  type FixWithCopilotDialogProps,
} from '@github-ui/copilot-apply-changes-button/FixWithCopilotDialog'
import {
  UnifiedCommentActionTelemetryEvents,
  type SendUnifiedCommentActionTelemetry,
} from './unified-comment-action-telemetry'

type UnifiedBatchItem = Parameters<LayoutStoreState['unifiedBatchActions']['addItem']>[0]
type UnifiedFixWithCopilotDialogProps = Omit<FixWithCopilotDialogProps, 'renderTrigger' | 'title'>

export interface UnifiedCommentActionsProps {
  batchItem?: UnifiedBatchItem
  fixWithCopilotDialogProps: UnifiedFixWithCopilotDialogProps
  isThreadResolved: boolean
  onAddToBatch?: (item: UnifiedBatchItem) => void
  onAddToBatchError?: (reason: string) => void
  onRemoveFromBatch?: (item: UnifiedBatchItem) => void
  onResolve: (resolutionReason?: ResolutionReasonType) => void
  onUnresolve: () => void
  resolvedBy?: string | null
  resolveButtonLabel?: string
  showBatchToggle?: boolean
  showResolutionReasons?: boolean
  onSendTelemetry?: SendUnifiedCommentActionTelemetry
  unresolveButtonLabel?: string
}

export function UnifiedCommentActions({
  batchItem,
  fixWithCopilotDialogProps,
  isThreadResolved,
  onAddToBatch,
  onAddToBatchError,
  onRemoveFromBatch,
  onResolve,
  onUnresolve,
  resolvedBy,
  resolveButtonLabel = 'Resolve comment',
  showBatchToggle = true,
  showResolutionReasons = false,
  onSendTelemetry,
  unresolveButtonLabel = 'Unresolve comment',
}: UnifiedCommentActionsProps) {
  const handleResolve = useCallback(
    (resolutionReason?: ResolutionReasonType) => {
      onSendTelemetry?.(UnifiedCommentActionTelemetryEvents.resolveThreadClicked)
      if (resolutionReason === undefined) {
        onResolve()
      } else {
        onResolve(resolutionReason)
      }
    },
    [onResolve, onSendTelemetry],
  )

  return (
    <div className="d-flex flex-items-center p-2 border-top flex-wrap gap-2" data-testid="unified-comment-actions">
      {isThreadResolved ? (
        <>
          <Button
            aria-label={unresolveButtonLabel}
            onClick={onUnresolve}
            data-testid="unified-comment-unresolve-button"
          >
            Unresolve comment
          </Button>
          {resolvedBy && (
            <span className="fgColor-muted f6">
              <b>{resolvedBy}</b> marked this thread as resolved
            </span>
          )}
        </>
      ) : (
        <>
          {showResolutionReasons ? (
            <ResolveThreadSplitButton resolveButtonLabel={resolveButtonLabel} onResolve={handleResolve} />
          ) : (
            <Button
              aria-label={resolveButtonLabel}
              onClick={() => handleResolve()}
              data-testid="unified-comment-resolve-button"
            >
              Resolve comment
            </Button>
          )}
          <FixWithCopilotDialog
            {...fixWithCopilotDialogProps}
            title="Fix with Copilot"
            renderTrigger={({onClick, taskUrl}) =>
              taskUrl ? (
                <Button as="a" href={taskUrl} leadingVisual={AgentIcon} className="no-underline">
                  View agent session
                </Button>
              ) : (
                <Button
                  leadingVisual={AgentIcon}
                  onClick={() => {
                    onSendTelemetry?.(UnifiedCommentActionTelemetryEvents.fixWithCopilotClicked)
                    onClick()
                  }}
                  data-testid="unified-comment-fix-with-copilot-button"
                >
                  Fix with Copilot
                </Button>
              )
            }
          />
          {showBatchToggle && batchItem && (
            <UnifiedBatchToggleButton
              batchItem={batchItem}
              onAddToBatch={onAddToBatch}
              onAddToBatchError={onAddToBatchError}
              onRemoveFromBatch={onRemoveFromBatch}
              onSendTelemetry={onSendTelemetry}
            />
          )}
        </>
      )}
    </div>
  )
}

type UnifiedBatchToggleButtonProps = {
  batchItem: UnifiedBatchItem
} & Pick<UnifiedCommentActionsProps, 'onAddToBatch' | 'onAddToBatchError' | 'onRemoveFromBatch' | 'onSendTelemetry'>

function UnifiedBatchToggleButton({
  batchItem,
  onAddToBatch,
  onAddToBatchError,
  onRemoveFromBatch,
  onSendTelemetry,
}: UnifiedBatchToggleButtonProps) {
  const batchId = useMemo(
    () => ({
      commentDatabaseId: batchItem.commentDatabaseId,
      suggestionIndex: batchItem.suggestionIndex,
    }),
    [batchItem.commentDatabaseId, batchItem.suggestionIndex],
  )
  const isInBatch = useIsInUnifiedBatch(batchId)
  const {addItem, removeItem} = useUnifiedBatchActions()
  const openManageBatchPanel = useOpenManageBatchPanel()
  const addToBatchButtonRef = useRef<HTMLButtonElement>(null)

  const handleClick = useCallback(() => {
    if (isInBatch) {
      onSendTelemetry?.(UnifiedCommentActionTelemetryEvents.removeFromBatchClicked)
      removeItem(batchId)
      onRemoveFromBatch?.(batchItem)
      return
    }

    onSendTelemetry?.(UnifiedCommentActionTelemetryEvents.addToBatchClicked)
    const result = addItem(batchItem)
    if (!result.ok) {
      onAddToBatchError?.(result.reason)
      return
    }

    onAddToBatch?.(batchItem)
    openManageBatchPanel({returnFocusTo: addToBatchButtonRef.current})
  }, [
    addItem,
    batchId,
    batchItem,
    isInBatch,
    onAddToBatch,
    onAddToBatchError,
    onRemoveFromBatch,
    onSendTelemetry,
    openManageBatchPanel,
    removeItem,
  ])

  return (
    <Button
      variant={isInBatch ? 'danger' : 'default'}
      onClick={handleClick}
      ref={addToBatchButtonRef}
      data-testid="unified-comment-batch-button"
    >
      {isInBatch ? 'Remove from batch' : 'Add to batch'}
    </Button>
  )
}
